import React, { useEffect } from 'react';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  cityName?: string;
  canonical?: string;
  structuredData?: object;
}

export const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description,
  keywords,
  cityName = "Long Island",
  canonical,
  structuredData
}) => {
  const defaultTitle = `Professional Roofing Services | Safeway Roofing & Chimney`;
  const cityTitle = cityName !== "Long Island" ? `Roof Repair in ${cityName} | #1 Roofing Experts` : defaultTitle;
  const finalTitle = title || cityTitle;
  
  const defaultDescription = `Professional roofing services in ${cityName}, NY. 20+ years experience, GAF certified contractor. Emergency roof repair, roof replacement, gutter services. Call (631) 710-7600 for free estimate!`;
  const finalDescription = description || defaultDescription;
  
  const defaultKeywords = `roofing contractor ${cityName}, roof repair ${cityName}, roof replacement ${cityName}, emergency roof repair, gutter installation, chimney repair, GAF certified roofer`;
  const finalKeywords = keywords || defaultKeywords;

  // Base structured data for LocalBusiness
  const baseStructuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "LocalBusiness",
        "@id": "https://quote.safewayroofingandchimney.com/#business",
        "name": "Safeway Roofing & Chimney",
        "description": "Professional roofing contractor serving Long Island with 20+ years of experience. GAF Master Elite certified.",
        "url": "https://quote.safewayroofingandchimney.com",
        "telephone": "(631) 710-7600",
        "email": "info@safewayroofingandchimney.com",
        "address": {
          "@type": "PostalAddress",
          "addressRegion": "NY",
          "addressCountry": "US",
          "addressLocality": "Long Island"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": "40.7891",
          "longitude": "-73.1350"
        },
        "areaServed": [
          {
            "@type": "City",
            "name": "Nassau County"
          },
          {
            "@type": "City", 
            "name": "Suffolk County"
          },
          {
            "@type": "City",
            "name": "Long Island"
          }
        ],
        "serviceType": "Roofing Contractor",
        "hasOffers": [
          {
            "@type": "Offer",
            "name": "Emergency Roof Repair",
            "description": "24/7 emergency roofing services"
          },
          {
            "@type": "Offer", 
            "name": "Roof Replacement",
            "description": "Complete roof replacement with premium materials"
          },
          {
            "@type": "Offer",
            "name": "Gutter Services", 
            "description": "Gutter installation, repair, and cleaning"
          }
        ],
        "openingHours": "Mo-Su 00:00-23:59",
        "priceRange": "$$",
        "image": "https://quote.safewayroofingandchimney.com/images/safeway-roofing-logo.png",
        "logo": "https://quote.safewayroofingandchimney.com/images/safeway-roofing-logo.png",
        "sameAs": [
          "https://www.facebook.com/safewayroofingandchimney",
          "https://www.instagram.com/safewayroofingandchimney"
        ]
      },
      {
        "@type": "Organization",
        "@id": "https://quote.safewayroofingandchimney.com/#organization",
        "name": "Safeway Roofing & Chimney",
        "url": "https://quote.safewayroofingandchimney.com",
        "logo": {
          "@type": "ImageObject",
          "url": "https://quote.safewayroofingandchimney.com/images/safeway-roofing-logo.png"
        },
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": "(631) 710-7600",
          "contactType": "customer service",
          "availableLanguage": "English"
        }
      },
      {
        "@type": "WebSite",
        "@id": "https://quote.safewayroofingandchimney.com/#website",
        "url": "https://quote.safewayroofingandchimney.com",
        "name": "Safeway Roofing & Chimney",
        "description": "Professional roofing services in Long Island, NY",
        "potentialAction": {
          "@type": "SearchAction",
          "target": "https://quote.safewayroofingandchimney.com/?s={search_term_string}",
          "query-input": "required name=search_term_string"
        }
      }
    ]
  };

  const finalStructuredData = structuredData || baseStructuredData;

  useEffect(() => {
    // Update document title
    document.title = finalTitle;

    // Add noindex meta tag for welcome page
    const robotsTag = document.querySelector('meta[name="robots"]');
    if (robotsTag) {
      robotsTag.setAttribute('content', 'noindex,follow');
    } else {
      const newRobotsTag = document.createElement('meta');
      newRobotsTag.name = 'robots';
      newRobotsTag.content = 'noindex,follow';
      document.head.appendChild(newRobotsTag);
    }

    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', finalDescription);
    } else {
      const newMetaDescription = document.createElement('meta');
      newMetaDescription.name = 'description';
      newMetaDescription.content = finalDescription;
      document.head.appendChild(newMetaDescription);
    }

    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', finalKeywords);
    } else {
      const newMetaKeywords = document.createElement('meta');
      newMetaKeywords.name = 'keywords';
      newMetaKeywords.content = finalKeywords;
      document.head.appendChild(newMetaKeywords);
    }

    // Update Open Graph tags
    const updateOrCreateOGTag = (property: string, content: string) => {
      let ogTag = document.querySelector(`meta[property="${property}"]`);
      if (ogTag) {
        ogTag.setAttribute('content', content);
      } else {
        ogTag = document.createElement('meta');
        ogTag.setAttribute('property', property);
        ogTag.setAttribute('content', content);
        document.head.appendChild(ogTag);
      }
    };

    updateOrCreateOGTag('og:title', finalTitle);
    updateOrCreateOGTag('og:description', finalDescription);
    updateOrCreateOGTag('og:image', '/og-cover.jpg');
    updateOrCreateOGTag('og:type', 'website');

    // Add Twitter Card tags
    const updateOrCreateTwitterTag = (name: string, content: string) => {
      let twitterTag = document.querySelector(`meta[name="${name}"]`);
      if (twitterTag) {
        twitterTag.setAttribute('content', content);
      } else {
        twitterTag = document.createElement('meta');
        twitterTag.setAttribute('name', name);
        twitterTag.setAttribute('content', content);
        document.head.appendChild(twitterTag);
      }
    };

    updateOrCreateTwitterTag('twitter:card', 'summary_large_image');
    updateOrCreateTwitterTag('twitter:title', finalTitle);
    updateOrCreateTwitterTag('twitter:description', finalDescription);
    updateOrCreateTwitterTag('twitter:image', '/og-cover.jpg');

    // Update canonical URL
    if (canonical) {
      let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (canonicalLink) {
        canonicalLink.setAttribute('href', canonical);
      } else {
        const newCanonicalLink = document.createElement('link');
        newCanonicalLink.rel = 'canonical';
        newCanonicalLink.href = canonical;
        document.head.appendChild(newCanonicalLink);
      }
    }

    // Update structured data
    let structuredDataScript = document.getElementById('structured-data') as HTMLScriptElement;
    if (structuredDataScript) {
      structuredDataScript.textContent = JSON.stringify(finalStructuredData, null, 2);
    } else {
      const newStructuredDataScript = document.createElement('script');
      newStructuredDataScript.id = 'structured-data';
      newStructuredDataScript.type = 'application/ld+json';
      newStructuredDataScript.textContent = JSON.stringify(finalStructuredData, null, 2);
      document.head.appendChild(newStructuredDataScript);
    }
  }, [finalTitle, finalDescription, finalKeywords, canonical, finalStructuredData]);

  return null;
};